import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { VersionGroupProps, DisplayMode } from "./types";
import { ProjectVersion } from "@/stores/versioning/versioning-store";
import { VersionNode } from "./VersionNode";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronDown,
  ChevronUp,
  Calendar,
  User,
  GitBranch,
  Package,
} from "lucide-react";

const getGroupIcon = (title: string, displayMode: DisplayMode) => {
  // Determine icon based on group title or display mode
  if (
    title.includes("@") ||
    title.includes("user") ||
    title.toLowerCase().includes("by")
  ) {
    return <User className="w-4 h-4" />;
  }
  if (title.includes("/") || title.includes("-") || title.includes("20")) {
    return <Calendar className="w-4 h-4" />;
  }
  if (title.toLowerCase().includes("branch")) {
    return <GitBranch className="w-4 h-4" />;
  }
  return <Package className="w-4 h-4" />;
};

const formatGroupTitle = (title: string): string => {
  // Format different types of group titles
  if (title.includes("/")) {
    // Date format like "12/25/2024"
    try {
      const date = new Date(title);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return title;
    }
  }

  if (title === "All Versions") {
    return "All Versions";
  }

  return title;
};

export const VersionGroup: React.FC<VersionGroupProps> = ({
  title,
  versions,
  currentVersion,
  selectedVersions,
  displayMode,
  showActions,
  onVersionSelect,
  onVersionRestore,
  onVersionCompare,
  onVersionDelete,
  onVersionPublish,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const formattedTitle = formatGroupTitle(title);
  const groupIcon = getGroupIcon(title, displayMode);

  // Sort versions by version number (latest first)
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

  // Calculate group statistics
  const stats = {
    total: versions.length,
    published: versions.filter((v) => v.isPublished).length,
    current: versions.filter((v) => v.version === currentVersion).length,
    drafts: versions.filter((v) => !v.isPublished).length,
  };

  const renderTimelineView = () => (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

      <div className="space-y-4">
        {sortedVersions.map((version, index) => (
          <div key={version.version} className="relative">
            {/* Timeline node */}
            <div
              className={cn(
                "absolute left-4 w-4 h-4 rounded-full border-2 bg-background z-10",
                version.version === currentVersion
                  ? "border-green-500 bg-green-500"
                  : version.isPublished
                    ? "border-blue-500"
                    : "border-orange-500",
              )}
            />

            {/* Version node */}
            <div className="ml-12">
              <VersionNode
                version={version}
                isSelected={selectedVersions.has(version.version.toString())}
                isCurrent={version.version === currentVersion}
                showActions={showActions}
                displayMode={displayMode}
                onSelect={() => onVersionSelect(version)}
                onRestore={() => onVersionRestore?.(version)}
                onCompare={() => onVersionCompare?.(version)}
                onDelete={() => onVersionDelete?.(version)}
                onPublish={() => onVersionPublish?.(version)}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderListView = () => (
    <div className="space-y-2">
      {sortedVersions.map((version) => (
        <VersionNode
          key={version.version}
          version={version}
          isSelected={selectedVersions.has(version.version.toString())}
          isCurrent={version.version === currentVersion}
          showActions={showActions}
          displayMode={displayMode}
          onSelect={() => onVersionSelect(version)}
          onRestore={() => onVersionRestore?.(version)}
          onCompare={() => onVersionCompare?.(version)}
          onDelete={() => onVersionDelete?.(version)}
          onPublish={() => onVersionPublish?.(version)}
        />
      ))}
    </div>
  );

  const renderGridView = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {sortedVersions.map((version) => (
        <VersionNode
          key={version.version}
          version={version}
          isSelected={selectedVersions.has(version.version.toString())}
          isCurrent={version.version === currentVersion}
          showActions={showActions}
          displayMode={displayMode}
          onSelect={() => onVersionSelect(version)}
          onRestore={() => onVersionRestore?.(version)}
          onCompare={() => onVersionCompare?.(version)}
          onDelete={() => onVersionDelete?.(version)}
          onPublish={() => onVersionPublish?.(version)}
        />
      ))}
    </div>
  );

  const renderVersions = () => {
    switch (displayMode) {
      case "timeline":
        return renderTimelineView();
      case "list":
        return renderListView();
      case "grid":
        return renderGridView();
      default:
        return renderListView();
    }
  };

  return (
    <div className="border rounded-lg bg-card">
      {/* Group Header */}
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="w-full justify-between p-4 h-auto hover:bg-muted/50"
            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${formattedTitle} group`}
          >
            <div className="flex items-center space-x-3">
              {groupIcon}
              <div className="text-left">
                <h3 className="font-semibold text-base">{formattedTitle}</h3>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground mt-1">
                  <span>
                    {stats.total} version{stats.total !== 1 ? "s" : ""}
                  </span>
                  {stats.published > 0 && (
                    <>
                      <span>•</span>
                      <span>{stats.published} published</span>
                    </>
                  )}
                  {stats.drafts > 0 && (
                    <>
                      <span>•</span>
                      <span>
                        {stats.drafts} draft{stats.drafts !== 1 ? "s" : ""}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Status badges */}
              <div className="flex items-center space-x-1">
                {stats.current > 0 && (
                  <Badge
                    variant="default"
                    className="bg-green-500 hover:bg-green-600"
                  >
                    Current
                  </Badge>
                )}
                {stats.published > 0 && (
                  <Badge variant="secondary">{stats.published} Published</Badge>
                )}
                {stats.drafts > 0 && (
                  <Badge variant="outline">
                    {stats.drafts} Draft{stats.drafts !== 1 ? "s" : ""}
                  </Badge>
                )}
              </div>

              {/* Expand/Collapse icon */}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </div>
          </Button>
        </CollapsibleTrigger>

        {/* Group Content */}
        <CollapsibleContent>
          <div className="px-4 pb-4">
            {versions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No versions in this group
              </div>
            ) : (
              renderVersions()
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default VersionGroup;
